import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, useTheme } from "../App";
import {
  ArrowLeft,
  Clock,
  Calendar,
  User,
  Share2,
  Heart,
  MessageCircle,
  Bookmark,
  Moon,
  Sun,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      // First try to get from articles
      const articlesResponse = await fetch(`${API}/cms/articles`);
      if (articlesResponse.ok) {
        const articles = await articlesResponse.json();
        const foundArticle = articles.find(a => a.id === id);
        if (foundArticle) {
          setArticle(foundArticle);
          setRelatedArticles(articles.filter(a => a.id !== id).slice(0, 3));
          setLoading(false);
          return;
        }
      }
      
      // Then try to get from heroes
      const heroesResponse = await fetch(`${API}/cms/heroes`);
      if (heroesResponse.ok) {
        const heroes = await heroesResponse.json();
        const foundHero = heroes.find(h => h.id === id);
        if (foundHero) {
          setArticle(foundHero);
          // Get related from both heroes and articles
          const articlesRes = await fetch(`${API}/cms/articles`);
          const articles = articlesRes.ok ? await articlesRes.json() : [];
          const otherHeroes = heroes.filter(h => h.id !== id);
          setRelatedArticles([...otherHeroes, ...articles].slice(0, 3));
          setLoading(false);
          return;
        }
      }
      
      // If not found anywhere, try direct API
      const response = await fetch(`${API}/articles/${id}`);
      if (response.ok) {
        const data = await response.json();
        setArticle(data);
      }
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title,
          text: article?.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Lien copié !");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">Article non trouvé</h1>
          <Button onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="article-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <Link to="/" className="font-heading text-xl font-black text-primary">
            BAOULÉ MÉDIA
          </Link>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Category & Meta */}
        <div className="mb-6">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4">
            {article.category}
          </span>
          
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-tight">
            {article.title}
          </h1>

          <p className="text-xl text-muted-foreground mb-6">
            {article.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-6 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent"></div>
              <div>
                <p className="font-semibold text-foreground">{article.author}</p>
                <p>{article.time || article.date}</p>
              </div>
            </div>
            {article.read_time && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {article.read_time}
              </div>
            )}
          </div>
        </div>

        {/* Featured Image */}
        {article.image_url && (
          <figure className="mb-8 -mx-4 sm:mx-0">
            <img
              src={getImageUrl(article.image_url)}
              alt={article.title}
              className="w-full h-64 sm:h-96 object-cover sm:rounded-xl"
            />
            <figcaption className="text-center text-sm text-muted-foreground mt-2">
              {article.image_caption || "Image illustrative"}
            </figcaption>
          </figure>
        )}

        {/* Article Body */}
        <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
          {article.content ? (
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          ) : (
            <div className="space-y-6">
              <p>
                {article.excerpt}
              </p>
              <p>
                La langue baoulé, parlée par plus de 2 millions de personnes en Côte d'Ivoire, 
                est un patrimoine culturel vivant qui mérite d'être préservé et transmis aux 
                générations futures. Cette initiative s'inscrit dans un mouvement plus large 
                de valorisation des langues africaines.
              </p>
              <h2>L'importance de la préservation linguistique</h2>
              <p>
                Les langues sont plus que de simples outils de communication. Elles portent 
                en elles l'histoire, les traditions, les proverbes et la sagesse d'un peuple. 
                Le baoulé, avec ses tonalités musicales et ses expressions imagées, offre 
                une fenêtre unique sur la culture ivoirienne.
              </p>
              <blockquote>
                "Celui qui connaît sa langue connaît son histoire" - Proverbe baoulé
              </blockquote>
              <h2>Les nouvelles technologies au service de la tradition</h2>
              <p>
                Grâce aux outils numériques modernes, l'apprentissage du baoulé devient 
                accessible à tous. Les applications mobiles, les podcasts et les plateformes 
                comme Baoulé Média permettent de toucher une nouvelle génération d'apprenants, 
                qu'ils soient en Côte d'Ivoire ou dans la diaspora.
              </p>
              <p>
                Les flashcards interactives, les quiz quotidiens et le chatbot IA constituent 
                des méthodes d'apprentissage innovantes qui rendent l'expérience à la fois 
                ludique et efficace.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between py-6 border-t border-b border-border mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLiked(!liked)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                liked ? "bg-red-500/10 text-red-500" : "hover:bg-muted"
              }`}
            >
              <Heart className={`w-5 h-5 ${liked ? "fill-current" : ""}`} />
              <span>{liked ? "Aimé" : "J'aime"}</span>
            </button>
            <button
              onClick={() => setSaved(!saved)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                saved ? "bg-primary/10 text-primary" : "hover:bg-muted"
              }`}
            >
              <Bookmark className={`w-5 h-5 ${saved ? "fill-current" : ""}`} />
              <span>{saved ? "Enregistré" : "Enregistrer"}</span>
            </button>
          </div>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-muted transition-colors"
          >
            <Share2 className="w-5 h-5" />
            <span className="hidden sm:inline">Partager</span>
          </button>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section>
            <h2 className="font-heading text-2xl font-bold mb-6">Articles similaires</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group"
                >
                  <div className="aspect-video rounded-lg overflow-hidden mb-3">
                    <img
                      src={getImageUrl(related.image_url)}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <span className="text-xs font-bold text-primary">{related.category}</span>
                  <h3 className="font-bold group-hover:text-primary transition-colors line-clamp-2">
                    {related.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* CTA */}
      <section className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl font-bold mb-4">
            Apprenez le Baoulé dès aujourd'hui
          </h2>
          <p className="mb-6 opacity-90">
            Rejoignez des milliers d'apprenants et découvrez la richesse de la langue baoulé
          </p>
          <Button
            onClick={() => navigate(user ? "/learn" : "/register")}
            variant="secondary"
            size="lg"
          >
            Commencer gratuitement
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
